import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// ============================================================================
// Types
// ============================================================================

export interface UserRole {
  id: number;
  name: string;
}

export interface EmployeeId {
  id: number;
  employeeId?: number;
  userType?: number;
  profilePicture?: string;
}

export interface User {
  access_token: string;
  expires_in?: number;
  userName?: string;
  email: string;
  partyId: number;
  firstName: string;
  lastName: string;
  contactNo?: string;
  userId: number;
  employeeId?: EmployeeId;
  partyType?: number;
  roles: UserRole[];
  role?: UserRole; // Some responses use singular 'role'
  immediateSupervisorPartyId?: number | null;
  jobDeatils?: {
    peopleManager?: string;
    [key: string]: any;
  };
  operations?: any[];
  clientId?: number;
  clientName?: string;
  tenantId?: number; // Tenant ID from JWT
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  sessionChecked: boolean;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface GoogleLoginPayload {
  token: string;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  sessionChecked: false
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Save user session to sessionStorage (maintains backward compatibility)
 */
const saveSessionToStorage = (user: User) => {
  sessionStorage.setItem('LoginInfo', JSON.stringify({ login: true }));
  sessionStorage.setItem('UserDetail', JSON.stringify(user));
  sessionStorage.setItem('UserEmail', JSON.stringify(user.email));
  sessionStorage.setItem('UserpartyId', JSON.stringify(user.partyId));
  sessionStorage.setItem('UserId', user.userId?.toString() || '');
  sessionStorage.setItem('employeeId', user.employeeId?.id?.toString() || '');
  sessionStorage.setItem('operations', JSON.stringify(user.operations || []));
  sessionStorage.setItem('immediateSupervisorPartyId', JSON.stringify(user.immediateSupervisorPartyId));
  sessionStorage.setItem('peopleManager', JSON.stringify(user.jobDeatils?.peopleManager));
};

/**
 * Save auth token to cookie
 */
const saveTokenToCookie = (token: string) => {
  const expires = new Date();
  expires.setDate(expires.getDate() + 1);
  document.cookie = `auth_token=${token}; expires=${expires.toUTCString()}; path=/; SameSite=Strict`;
};

/**
 * Clear all session data
 * Note: Does NOT clear localStorage tenant persistence (lastUsedTenant)
 * so users return to their last tenant when they log back in.
 * Call clearStoredTenant() from tenantUtils if you need to clear that too.
 */
const clearAllSession = () => {
  sessionStorage.clear();
  document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  // Intentionally NOT clearing localStorage.getItem('lastUsedTenant') for user convenience
};

/**
 * Get auth token from cookie
 */
const getTokenFromCookie = (): string | null => {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'auth_token') {
      return value;
    }
  }
  return null;
};

/**
 * Try to load user from sessionStorage on initial load
 */
const loadUserFromStorage = (): User | null => {
  try {
    const userDetailString = sessionStorage.getItem('UserDetail');
    if (userDetailString) {
      return JSON.parse(userDetailString) as User;
    }
  } catch (e) {
    console.error('Failed to parse UserDetail from sessionStorage:', e);
  }
  return null;
};

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Login with email/username and password
 */
export const loginUser = createAsyncThunk<User, LoginCredentials, { rejectValue: string }>(
  'auth/loginUser',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, credentials);

      if (response.status !== 200 && response.status !== 201) {
        return rejectWithValue('Invalid credentials');
      }

      const user: User = response.data;

      if (!user.access_token) {
        return rejectWithValue('No access token received');
      }

      // Save to storage for backward compatibility
      saveSessionToStorage(user);
      saveTokenToCookie(user.access_token);

      return user;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Login failed';
      return rejectWithValue(message);
    }
  }
);

/**
 * Login with Google OAuth
 */
export const loginWithGoogle = createAsyncThunk<User, GoogleLoginPayload, { rejectValue: string }>(
  'auth/loginWithGoogle',
  async ({ token }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/google-login`, { token });

      if (response.status !== 200 && response.status !== 201) {
        return rejectWithValue('Google login failed');
      }

      const user: User = response.data;

      if (!user.access_token) {
        return rejectWithValue('No access token received');
      }

      // Save to storage for backward compatibility
      saveSessionToStorage(user);
      saveTokenToCookie(user.access_token);

      return user;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Google login failed';
      return rejectWithValue(message);
    }
  }
);

/**
 * Restore session from cookie token
 */
export const restoreSession = createAsyncThunk<User, void, { rejectValue: string }>(
  'auth/restoreSession',
  async (_, { rejectWithValue }) => {
    try {
      // First try to load from sessionStorage
      const storedUser = loadUserFromStorage();
      const tokenFromCookie = getTokenFromCookie();

      // If we have user in storage and token in cookie, validate with backend
      const tokenToValidate = storedUser?.access_token || tokenFromCookie;

      if (!tokenToValidate) {
        return rejectWithValue('No token found');
      }

      const response = await axios.post(
        `${API_BASE_URL}/auth/restore-session`,
        { token: tokenToValidate },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (!response.data.isValid || !response.data.user) {
        clearAllSession();
        return rejectWithValue('Session expired');
      }

      const user: User = response.data.user;

      // Update storage with fresh data
      saveSessionToStorage(user);
      if (tokenToValidate) {
        saveTokenToCookie(tokenToValidate);
      }

      return user;
    } catch (error: any) {
      clearAllSession();
      return rejectWithValue('Session restoration failed');
    }
  }
);

/**
 * Logout user
 */
export const logoutUser = createAsyncThunk<void, void>('auth/logoutUser', async () => {
  clearAllSession();
  // Optionally call backend logout endpoint
  // await axios.post(`${API_BASE_URL}/auth/logout`);
});

// ============================================================================
// Slice
// ============================================================================

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Synchronously set user (useful for hydration from sessionStorage)
     */
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      state.token = action.payload?.access_token || null;
      state.isAuthenticated = !!action.payload;
    },

    /**
     * Clear any auth errors
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Mark session as checked (prevents infinite loops on public routes)
     */
    markSessionChecked: (state) => {
      state.sessionChecked = true;
    },

    /**
     * Hydrate state from sessionStorage on app init
     * Also marks session as checked to prevent unnecessary restoreSession calls
     */
    hydrateFromStorage: (state) => {
      const user = loadUserFromStorage();
      if (user) {
        state.user = user;
        state.token = user.access_token;
        state.isAuthenticated = true;
        state.sessionChecked = true; // Prevent unnecessary restoreSession call
      }
    }
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.token = action.payload.access_token;
        state.isAuthenticated = true;
        state.error = null;
        state.sessionChecked = true;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Login failed';
        state.isAuthenticated = false;
      });

    // Google Login
    builder
      .addCase(loginWithGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.token = action.payload.access_token;
        state.isAuthenticated = true;
        state.error = null;
        state.sessionChecked = true;
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Google login failed';
        state.isAuthenticated = false;
      });

    // Restore Session
    builder
      .addCase(restoreSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.token = action.payload.access_token;
        state.isAuthenticated = true;
        state.sessionChecked = true;
      })
      .addCase(restoreSession.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.sessionChecked = true;
      });

    // Logout
    builder.addCase(logoutUser.fulfilled, (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.sessionChecked = true;
    });
  }
});

// ============================================================================
// Exports
// ============================================================================

export const { setUser, clearError, markSessionChecked, hydrateFromStorage } = authSlice.actions;

export default authSlice.reducer;

// ============================================================================
// Selectors
// ============================================================================

// Note: RootState type will be inferred from store.tsx
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.loading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectSessionChecked = (state: { auth: AuthState }) => state.auth.sessionChecked;
export const selectUserRoleId = (state: { auth: AuthState }) => state.auth.user?.roles?.[0]?.id || state.auth.user?.role?.id || null;
